// CIP fork feature (see FORK.md): who else is in this project, where, and
// whether they are here right now. Pure: the wire shapes, the status rules
// and the labels. See ../README.md for the model.
import { z } from "zod";

/** A heartbeat older than this means the tab is gone or asleep. */
export const LIVE_WINDOW_MS = 40_000;
/** No input for this long and a visible tab reads as idle. */
export const IDLE_AFTER_MS = 60_000;
/** Someone seen within this window still shows, dimmed. Beyond it, gone. */
export const RECENT_WINDOW_MS = 10 * 60_000;

export const BEAT_MS = 10_000;
/** While someone else is active on the same resource: snappier peers. */
export const FAST_BEAT_MS = 4_000;
export const HIDDEN_BEAT_MS = 30_000;
/** How long an edit keeps someone "editing". */
export const EDITING_WINDOW_MS = 3_000;

export const PresenceFocusSchema = z.object({
  /** What is focused: a builder card, screen or chain, or the form itself. */
  kind: z.enum(["card", "screen", "chain", "form"]),
  id: z.string().max(128),
  /** The screen holding a focused card, so its frame can show the peer. */
  screenId: z.string().max(128).nullish(),
  label: z.string().max(160).nullish(),
});
export type PresenceFocus = z.infer<typeof PresenceFocusSchema>;

export const PresenceLocationSchema = z.object({
  /** The page, without query or hash: what "on the same page" compares. */
  path: z.string().max(512),
  /** Where "jump to" goes, query included (a builder tab, a filter). */
  href: z.string().max(1024),
  title: z.string().max(200),
  /** The record the page is about, when it is about one. */
  resource: z
    .object({ type: z.literal("elicitation"), id: z.string().max(64) })
    .nullish(),
  /** A sub-view of the page, such as the builder's tab. */
  view: z.string().max(40).nullish(),
});
export type PresenceLocation = z.infer<typeof PresenceLocationSchema>;

export const PresenceBeatSchema = z.object({
  projectId: z.string(),
  /** One per browser tab, so two tabs of one person are two entries. */
  clientId: z.string().min(8).max(64),
  location: PresenceLocationSchema,
  focus: PresenceFocusSchema.nullish(),
  editing: z.boolean(),
  visible: z.boolean(),
  /** Milliseconds since the last pointer or key input in this tab. */
  idleMs: z.number().int().nonnegative().max(86_400_000),
  /** The tab is closing or leaving the project: keep it, dimmed. */
  leaving: z.boolean().optional(),
  /**
   * When the tab sent this beat, on its own clock. A reload's goodbye beat
   * can reach the server after the new page's first one; the server keeps
   * whichever the tab sent last, so a person is never shown as gone from
   * the page they just left.
   */
  sentAt: z.number().int().nonnegative().optional(),
});
export type PresenceBeat = z.infer<typeof PresenceBeatSchema>;

/** One tab, as every member of the project sees it. */
export type PresenceEntry = {
  clientId: string;
  userId: string;
  name: string;
  image: string | null;
  location: PresenceLocation;
  focus: PresenceFocus | null;
  editing: boolean;
  visible: boolean;
  left: boolean;
  /** Ages, measured on the server's clock so client skew cannot matter. */
  seenAgoMs: number;
  activeAgoMs: number;
};

export type PresenceStatus = "active" | "idle";

export function statusOf(entry: PresenceEntry): PresenceStatus {
  return !entry.left &&
    entry.visible &&
    entry.seenAgoMs < LIVE_WINDOW_MS &&
    entry.activeAgoMs < IDLE_AFTER_MS
    ? "active"
    : "idle";
}

/** One person, from however many tabs they have open. */
export type Peer = {
  userId: string;
  name: string;
  image: string | null;
  status: PresenceStatus;
  /** The tab that best says where they are: active first, then freshest. */
  primary: PresenceEntry;
  /** Every live tab, for "on this page" and builder markers. */
  entries: PresenceEntry[];
  /** Time since they last did anything, across their tabs. */
  activeAgoMs: number;
};

/**
 * Collapses tabs into people, drops yourself and anyone gone longer than the
 * recent window, and orders active people first, then by recency.
 */
export function peersFrom(
  entries: PresenceEntry[],
  selfUserId: string | null,
): Peer[] {
  const byUser = new Map<string, PresenceEntry[]>();
  for (const entry of entries) {
    if (entry.userId === selfUserId) continue;
    if (entry.seenAgoMs > RECENT_WINDOW_MS) continue;
    const list = byUser.get(entry.userId) ?? [];
    list.push(entry);
    byUser.set(entry.userId, list);
  }
  const peers: Peer[] = [];
  for (const [userId, list] of byUser) {
    const ranked = [...list].sort(
      (a, b) =>
        rank(statusOf(a)) - rank(statusOf(b)) ||
        a.activeAgoMs - b.activeAgoMs ||
        a.seenAgoMs - b.seenAgoMs,
    );
    const primary = ranked[0]!;
    peers.push({
      userId,
      name: primary.name,
      image: primary.image,
      status: statusOf(primary),
      primary,
      entries: ranked,
      activeAgoMs: Math.min(...ranked.map((entry) => entry.activeAgoMs)),
    });
  }
  return peers.sort(
    (a, b) =>
      rank(a.status) - rank(b.status) ||
      a.activeAgoMs - b.activeAgoMs ||
      a.name.localeCompare(b.name),
  );
}

function rank(status: PresenceStatus): number {
  return status === "active" ? 0 : 1;
}

/** The peer's tabs that are on `path` right now (gone tabs excluded). */
/**
 * Whether a tab is still open somewhere: it has not said goodbye and is
 * still beating. A tab that stopped without a goodbye (a crash, a closed
 * lid) keeps its person in the facepile, dimmed, but is on no page.
 */
export function isLive(entry: PresenceEntry): boolean {
  return !entry.left && entry.seenAgoMs < LIVE_WINDOW_MS;
}

export function entriesOnPath(peer: Peer, path: string): PresenceEntry[] {
  return peer.entries.filter(
    (entry) => isLive(entry) && entry.location.path === path,
  );
}

/** The peer's tabs on one resource, e.g. one elicitation, whatever the tab. */
export function entriesOnResource(
  peer: Peer,
  resource: { type: "elicitation"; id: string },
): PresenceEntry[] {
  return peer.entries.filter(
    (entry) =>
      isLive(entry) &&
      entry.location.resource?.type === resource.type &&
      entry.location.resource.id === resource.id,
  );
}

/**
 * Person colours: dark enough that white text on them (a name flag, an
 * initial) reaches 4.5:1, and distinct enough to tell two people apart.
 */
export const PEER_COLORS = [
  "#175CD3", // blue
  "#B42318", // red
  "#067647", // green
  "#6927DA", // violet
  "#B93815", // orange
  "#0E7090", // cyan
  "#C11574", // pink
  "#3538CD", // indigo
  "#107569", // teal
  "#93370D", // amber
  "#4E5BA6", // slate blue
  "#9E165F", // plum
] as const;

/**
 * A stable colour per person, the same on every screen and for every viewer,
 * so a card's outline and a facepile ring point at the same person.
 */
export function peerColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return PEER_COLORS[hash % PEER_COLORS.length]!;
}

export function initialsOf(name: string): string {
  const parts = name
    .replace(/@.*$/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters =
    parts.length >= 2
      ? `${parts[0]![0]}${parts[parts.length - 1]![0]}`
      : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}

export function firstNameOf(name: string): string {
  return name.replace(/@.*$/, "").split(/\s+/)[0] || name;
}

/** "just now", "4m ago", "1h ago": how long since someone did anything. */
export function agoLabel(ms: number): string {
  if (ms < 60_000) return "just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

const SECTION_TITLES: Record<string, string> = {
  "": "Home",
  elicitations: "Elicitations",
  assemblies: "Assemblies",
  rubrics: "Rubrics",
  participants: "People",
  datasets: "Datasets",
  prompts: "Prompts",
  traces: "Tracing",
  observations: "Observations",
  sessions: "Sessions",
  users: "Users",
  scores: "Scores",
  dashboards: "Dashboards",
  evals: "Evaluators",
  experiments: "Experiments",
  "annotation-queues": "Annotation queues",
  playground: "Playground",
  settings: "Settings",
};

/**
 * A readable name for a project page from its path, for pages that do not
 * announce one: `/project/p1/datasets/d1/items` reads "Datasets".
 */
export function sectionTitle(path: string): string {
  const match = /^\/project\/[^/]+\/?([^/?#]*)/.exec(path);
  const section = match?.[1] ?? "";
  return (
    SECTION_TITLES[section] ??
    section.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}

/** The project id a path is inside, or null outside any project. */
export function projectIdOf(path: string): string | null {
  return /^\/project\/([^/?#]+)/.exec(path)?.[1] ?? null;
}
